// The three roads into a matter: the staff wizard for a client the office
// already knows how to file, the AI interview for a story that still has
// to be turned into facts, and the questionnaire link the client fills in
// on their own. The interview starts from text only - the
// file variants of /start are dead by construction (the JSON body cap is
// far below any scan), so documents join an interview once it exists.

import { Fragment, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  deleteIntakeSession,
  INTAKE_LIST_CAP,
  IntakeApiError,
  listIntakeSessions,
  remindIntake,
  resendIntakeLink,
  startInterview,
  takeIntoWork,
  type IntakeLinkIssue,
  type IntakeSessionSummary,
} from "../api/intake";
import { SendLinkDialog } from "../components/SendLinkDialog";
import { IssuedLinkReveal } from "../components/IssuedLinkReveal";
import { EntityLink } from "../../../components/chrome/EntityLink";
import { Skeleton, SkeletonGroup } from "../../../components/chrome/Skeleton";
import { useCurrentUser } from "../../../components/auth/CurrentUserContext";
import { isAdminRole } from "../../../lib/roles";
import { caseTypeLabel } from "../../../lib/caseLabels";
import { serverPracticeAreaLabel } from "../../../api/mock";
import { fmtDate } from "../../../lib/dates";
import { daysUntilDay } from "../../../api/firmDeadlines";

// Every row is under a day old by construction, so the day alone would
// read the same on all of them; the hour is what says how close to the
// 24-hour expiry an interview is.
function lastTurn(iso: string): string {
  return new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function IntakeHub() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [clientName, setClientName] = useState("");
  const [story, setStory] = useState("");
  const [linkOpen, setLinkOpen] = useState(false);
  const isAdmin = isAdminRole(useCurrentUser()?.role);
  const [resent, setResent] = useState<IntakeLinkIssue | null>(null);
  // The shared delete mutation reports into the card its action came
  // from, even after the refused row vanishes with a re-read.
  const [removeFrom, setRemoveFrom] = useState<"interview" | "link">("interview");

  const start = useMutation({
    mutationKey: ["intake", "start"],
    mutationFn: startInterview,
    onSuccess: (state) => {
      queryClient.invalidateQueries({ queryKey: ["intake", "sessions"] });
      navigate(`/intake/interview/${encodeURIComponent(state.sessionId)}`);
    },
  });

  const sessions = useQuery({ queryKey: ["intake", "sessions"], queryFn: listIntakeSessions });

  const resend = useMutation({
    mutationKey: ["intake", "resend"],
    mutationFn: resendIntakeLink,
    onSuccess: (issue) => {
      setResent(issue);
      queryClient.invalidateQueries({ queryKey: ["intake", "sessions"] });
      void queryClient.invalidateQueries({ queryKey: ["intake", "submission"] });
    },
  });

  const remind = useMutation({
    mutationKey: ["intake", "remind"],
    mutationFn: remindIntake,
    // Settled, not success: after a letter the row says when and by whom,
    // and a refusal means the row moved under a stale list (a colleague
    // reminded first, the client submitted).
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ["intake", "sessions"] });
      void queryClient.invalidateQueries({ queryKey: ["intake", "submission"] });
    },
  });

  const remove = useMutation({
    mutationKey: ["intake", "delete"],
    mutationFn: deleteIntakeSession,
    // Settled, not success: a 404 means the row is already gone server-side.
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ["intake", "sessions"] });
      void queryClient.invalidateQueries({ queryKey: ["intake", "submission"] });
    },
  });

  // A double click must not mint two takes: isPending flips only after
  // the next render, so the ref is the guard (as in ReviewBanner).
  const takeInFlight = useRef(false);
  const take = useMutation({
    mutationKey: ["intake", "take"],
    mutationFn: (vars: { sessionId: string; acknowledgedConflicts?: boolean }) =>
      takeIntoWork(vars.sessionId, vars.acknowledgedConflicts),
    onSuccess: () => {
      // A real matter with its client was minted and claimed: every
      // list served from memory for the next minute is stale.
      void queryClient.invalidateQueries({ queryKey: ["intake", "sessions"] });
      void queryClient.invalidateQueries({ queryKey: ["intake", "submission"] });
      void queryClient.invalidateQueries({ queryKey: ["matters"] });
      void queryClient.invalidateQueries({ queryKey: ["clients"] });
      void queryClient.invalidateQueries({ queryKey: ["firm", "counts"] });
    },
    onError: (err, vars) => {
      if (!(err instanceof IntakeApiError)) return;
      // The conflict gate refuses BEFORE the matter is minted, so a retry
      // with the taker's acknowledgement costs nothing.
      if (err.status === 409 && !vars.acknowledgedConflicts && /conflict/i.test(err.message)) {
        if (window.confirm(`${err.message}\n\nTake it into work anyway?`)) {
          take.mutate({ ...vars, acknowledgedConflicts: true });
        }
        return;
      }
      if (err.status !== 404 && err.status !== 409) return;
      // The row moved under a stale list: a 409 came after a mint or from
      // another taker, a 404 is a row a colleague took into a matter outside
      // the viewer's scope. Shown, never retried; the lists are read again.
      void queryClient.invalidateQueries({ queryKey: ["intake", "sessions"] });
      void queryClient.invalidateQueries({ queryKey: ["intake", "submission"] });
    },
    onSettled: () => {
      takeInFlight.current = false;
    },
  });

  const onTake = (s: IntakeSessionSummary) => {
    if (takeInFlight.current) return;
    takeInFlight.current = true;
    take.mutate({ sessionId: s.sessionId });
  };

  const onDelete = (s: IntakeSessionSummary) => {
    const who = s.clientName?.trim() ? ` for ${s.clientName.trim()}` : "";
    const ok = window.confirm(
      `Delete the interview${who}? The transcript is gone for good; files already uploaded are not deleted.`,
    );
    if (!ok) return;
    setRemoveFrom("interview");
    remove.mutate(s.sessionId);
  };

  const onRevoke = (s: IntakeSessionSummary) => {
    const who = s.clientName?.trim() ? ` for ${s.clientName.trim()}` : "";
    const ok = window.confirm(
      `Revoke the link${who}? The client's draft answers are deleted with it.`,
    );
    if (!ok) return;
    setRemoveFrom("link");
    remove.mutate(s.sessionId, {
      onSuccess: () => setResent((r) => (r?.sessionId === s.sessionId ? null : r)),
    });
  };

  const rows = sessions.data ?? [];
  const interviews = rows.filter((s) => s.kind !== "questionnaire");
  const links = rows.filter((s) => s.kind === "questionnaire");
  const canStart = story.trim().length > 0 && !start.isPending;

  return (
    <main className="cc-main">
      <div className="cc-body">
        <div className="cc-index-head">
          <span className="title">Client intake</span>
          <span className="blurb">
            Three roads to a matter - pick by what the office has in hand.
          </span>
          {isAdmin && (
            <Link to="/intake/questionnaire" className="cc-link-btn">
              Edit the questionnaire
            </Link>
          )}
        </div>

        <div className="intake-roads">
          <section className="cc-card intake-road" aria-label="Manual intake">
            <div className="cc-card-title">Manual intake</div>
            <p className="intake-prose">
              Identity, description, opposing parties, review. Conflict-checked on the way in; the
              matter is born active and opens to whoever files it.
            </p>
            <div className="cc-form-actions">
              <button
                type="button"
                className="cc-btn-primary"
                onClick={() => navigate("/intake/manual")}
              >
                Start manual intake
              </button>
            </div>
          </section>

          <section className="cc-card intake-road" aria-label="AI interview">
            <div className="cc-card-title">AI interview</div>
            <p className="intake-prose">
              Type or paste what the client told the office. The assistant reads it, asks follow-up
              questions and fills in the client's details; documents can be added along the way. The
              matter lands in the review queue for an attorney to take.
            </p>
            <label className="cc-form-field">
              <span className="cc-form-label">Client name</span>
              <input
                className="cc-field"
                aria-label="Client name"
                maxLength={120}
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
              />
              <span className="cc-form-hint">Optional - the interview can find it.</span>
            </label>
            <label className="cc-form-field">
              <span className="cc-form-label">The client's story</span>
              <textarea
                className="cc-field intake-textarea"
                aria-label="Client story"
                rows={6}
                maxLength={8000}
                placeholder="What happened, when, who is involved, and what the client is asking the firm to do..."
                value={story}
                onChange={(e) => setStory(e.target.value)}
              />
            </label>
            <div className="cc-form-actions">
              <button
                type="button"
                className="cc-btn-primary"
                disabled={!canStart}
                onClick={() =>
                  start.mutate({
                    content: story.trim(),
                    clientName: clientName.trim() || undefined,
                  })
                }
              >
                {start.isPending ? "Starting..." : "Start interview"}
              </button>
            </div>
            {start.isError && (
              <div role="alert" className="cc-form-error">
                {start.error.message}
              </div>
            )}
          </section>

          <section className="cc-card intake-road" aria-label="Questionnaire by link">
            <div className="cc-card-title">Questionnaire by link</div>
            <p className="intake-prose">
              The client fills the intake form in their own words and their own language, and hands
              over documents on the way. Submitting stages everything for review - nothing reaches
              the case files until someone takes it into work.
            </p>
            <p className="intake-prose">
              A client with no link starts from the public form at{" "}
              <code>{`${window.location.origin}/intake/new`}</code> and lands in the same list.
            </p>
            <div className="cc-form-actions">
              <button type="button" className="cc-btn-primary" onClick={() => setLinkOpen(true)}>
                Create an intake link
              </button>
            </div>
          </section>
        </div>

        <section className="cc-card intake-sessions" aria-label="Questionnaires">
          <div className="cc-card-head">
            <div>
              <div className="cc-card-title">Questionnaires</div>
              <div className="cc-card-sub">
                {sessions.isError
                  ? "The list could not be read."
                  : sessions.data
                    ? links.length === 0
                      ? "No questionnaire is out."
                      : questionnairesSub(links)
                    : "Reading the list"}
              </div>
            </div>
          </div>
          {sessions.isLoading && (
            <div className="intake-empty">
              <SkeletonGroup label="Reading questionnaire links">
                <Skeleton width="60%" />
                <Skeleton width="45%" />
              </SkeletonGroup>
            </div>
          )}
          {resend.isError && (
            <div role="alert" className="cc-form-error">
              {resend.error.message}
            </div>
          )}
          {remind.isError && (
            <div role="alert" className="cc-form-error">
              {remind.error.message}
            </div>
          )}
          {remove.isError && removeFrom === "link" && (
            <div role="alert" className="cc-form-error">
              {remove.error.message}
            </div>
          )}
          {take.isError && (
            <div role="alert" className="cc-form-error">
              {take.error.message}
            </div>
          )}
          {links.map((s) => (
            <Fragment key={s.sessionId}>
              <div className="intake-session-row">
                <div className="intake-session-who">
                  <span>
                    {s.clientName?.trim() || "(no name)"}
                    {s.source === "public" && <span className="cc-pill muted">Public form</span>}
                    <ClosingPill s={s} />
                  </span>
                  <span className="cc-form-hint">
                    {s.categoryLabel ?? s.category ?? "client picks the case type"} -{" "}
                    {s.email ?? "no email"}
                    {s.remindedAt ? reminded(s) : ""}
                  </span>
                </div>
                {s.status === "submitted" && s.matterEid ? (
                  <EntityLink
                    kind="matter"
                    id={s.matterEid}
                    eID={s.matterEid}
                    ariaLabel={`Submitted - open matter ${s.matterEid}`}
                  >
                    Submitted - open the matter
                  </EntityLink>
                ) : s.status === "submitted" ? (
                  <>
                    <span className="cc-form-hint">
                      {s.submittedAt ? `Submitted ${fmtDate(s.submittedAt)}` : "Submitted"}
                    </span>
                    <button
                      type="button"
                      className="cc-link-btn"
                      disabled={take.isPending}
                      onClick={() => onTake(s)}
                    >
                      Take into work
                    </button>
                  </>
                ) : (
                  <span className="cc-form-hint">{linkStatus(s)}</span>
                )}
                <Link
                  to={`/intake/submission/${encodeURIComponent(s.sessionId)}`}
                  className="cc-link-btn"
                  aria-label={`Answers - ${s.clientName?.trim() || "(no name)"}`}
                >
                  Answers
                </Link>
                {s.status !== "submitted" && (
                  <>
                    {/* A public row has no link to resend: a reminder sends
                        the client back to the public door instead. */}
                    {s.source === "public" ? (
                      <button
                        type="button"
                        className="cc-link-btn"
                        disabled={remind.isPending && remind.variables === s.sessionId}
                        onClick={() => remind.mutate(s.sessionId)}
                      >
                        Remind
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="cc-link-btn"
                        disabled={resend.isPending && resend.variables === s.sessionId}
                        onClick={() => resend.mutate(s.sessionId)}
                      >
                        Resend
                      </button>
                    )}
                    <button
                      type="button"
                      className="cc-link-btn danger"
                      disabled={remove.isPending && remove.variables === s.sessionId}
                      onClick={() => onRevoke(s)}
                    >
                      Revoke
                    </button>
                  </>
                )}
              </div>
              {resent?.sessionId === s.sessionId && (
                <div className="intake-reveal-row">
                  <span className="cc-form-hint">
                    The new link - shown once, the old one is dead:
                  </span>
                  <IssuedLinkReveal issue={resent} testId="resent-link" />
                </div>
              )}
            </Fragment>
          ))}
          {links.length >= INTAKE_LIST_CAP && (
            <div className="intake-empty">
              Showing {INTAKE_LIST_CAP} questionnaires: open ones first, then submitted ones waiting
              to be taken, then claimed ones; what did not fit is not listed.
            </div>
          )}
        </section>

        <section className="cc-card intake-sessions" aria-label="Unfinished interviews">
          <div className="cc-card-head">
            <div>
              <div className="cc-card-title">Unfinished interviews</div>
              <div className="cc-card-sub">
                {sessions.isError
                  ? "The list could not be read."
                  : sessions.data
                    ? `${interviews.length}${interviews.length >= INTAKE_LIST_CAP ? "+" : ""} open - an interview expires 24 hours after its last turn.`
                    : "Reading the list"}
              </div>
            </div>
          </div>
          {remove.isError && removeFrom === "interview" && (
            <div role="alert" className="cc-form-error">
              {remove.error.message}
            </div>
          )}
          {sessions.isLoading && (
            <div className="intake-empty">
              <SkeletonGroup label="Reading unfinished interviews">
                <Skeleton width="60%" />
                <Skeleton width="45%" />
              </SkeletonGroup>
            </div>
          )}
          {sessions.isError && (
            <div role="alert" className="cc-form-error">
              {sessions.error.message}
            </div>
          )}
          {sessions.data && interviews.length === 0 && (
            <div className="intake-empty">No interview is waiting.</div>
          )}
          {interviews.map((s) => (
            <div key={s.sessionId} className="intake-session-row">
              <div className="intake-session-who">
                <span>{s.clientName?.trim() || "(no name yet)"}</span>
                <span className="cc-form-hint">
                  {serverPracticeAreaLabel(s.practiceArea)}
                  {s.caseType ? ` / ${caseTypeLabel(s.caseType)}` : ""} - {s.keyFacts.length} fact
                  {s.keyFacts.length === 1 ? "" : "s"}
                </span>
              </div>
              <span className="cc-form-hint">
                {s.startedByEmail ?? "unknown"} - {lastTurn(s.updatedAt)}
              </span>
              <Link
                to={`/intake/interview/${encodeURIComponent(s.sessionId)}`}
                className="cc-link-btn"
              >
                Resume
              </Link>
              <button
                type="button"
                className="cc-link-btn danger"
                disabled={remove.isPending}
                onClick={() => onDelete(s)}
              >
                Delete
              </button>
            </div>
          ))}
          {interviews.length >= INTAKE_LIST_CAP && (
            <div className="intake-empty">
              Showing the latest {INTAKE_LIST_CAP} interviews; older ones are not listed.
            </div>
          )}
        </section>
      </div>
      {linkOpen && <SendLinkDialog onClose={() => setLinkOpen(false)} />}
    </main>
  );
}

// What is still out, by road; a submitted row is neither open nor dying.
function questionnairesSub(links: IntakeSessionSummary[]): string {
  const open = links.filter((s) => s.status !== "submitted");
  const fromPublic = open.filter((s) => s.source === "public").length;
  const byLink = open.length - fromPublic;
  const submitted = links.length - open.length;
  const parts: string[] = [];
  if (byLink > 0)
    parts.push(`${byLink} by link - a link dies after 14 days; resending mints a fresh one.`);
  if (fromPublic > 0) {
    parts.push(
      `${fromPublic} from the public form - open for 7 days from the client's code; a reminder starts the week again.`,
    );
  }
  if (submitted > 0) parts.push(`${submitted} submitted.`);
  return parts.join(" ");
}

// Who asked the client back, and when: the office's own act, on the row.
function reminded(s: IntakeSessionSummary): string {
  return ` - reminded ${fmtDate(s.remindedAt!)}${s.remindedBy ? ` by ${s.remindedBy}` : ""}`;
}

// How far the client got, against what their category asks.
function progress(s: IntakeSessionSummary): string {
  if (s.answered === null || s.questions === null) return "";
  return s.answered === 0 ? "no answers yet" : `${s.answered} of ${s.questions} answered`;
}

// A public row was born opened, so "Opened" would say nothing about it
// for its whole week: what ages is the deadline and the progress. A row
// touched after its birth (an answer, a second code) says when.
function linkStatus(s: IntakeSessionSummary): string {
  if (s.source === "public") {
    const parts = [s.expiresAt ? `Open until ${fmtDate(s.expiresAt)}` : "Open", progress(s)];
    if (Date.parse(s.updatedAt) - Date.parse(s.createdAt) > 60_000) {
      parts.push(`last active ${fmtDate(s.updatedAt)}`);
    }
    return parts.filter(Boolean).join(" - ");
  }
  if (s.openedAt) {
    const until = s.expiresAt ? `open until ${fmtDate(s.expiresAt)}` : "";
    return [`Opened ${lastTurn(s.openedAt)}`, progress(s), until].filter(Boolean).join(" - ");
  }
  return s.expiresAt ? `Sent - link dies ${fmtDate(s.expiresAt)}` : "Sent";
}

// The last two days of a questionnaire's life, said on the row: the day
// arithmetic is the deadline queue's, never instant subtraction.
function ClosingPill({ s }: { s: IntakeSessionSummary }) {
  if (!s.expiresAt) return null;
  // A questionnaire's deadline is an instant: one minted at UTC midnight
  // must not be read as a stored day.
  const days = daysUntilDay(s.expiresAt, false);
  if (days > 2) return null;
  const text = days < 0 ? "Closed" : days === 0 ? "Closes today" : `Closes in ${days}d`;
  return <span className={`cc-pill ${days <= 0 ? "crit" : "warn"}`}>{text}</span>;
}
